import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { DataTable } from '../../components/DataTable';
import { HasPermission } from '../../rbac/HasPermission';
import { safeExternalUrl } from '../../utils/safeUrl';
import {
  useDeleteRblProvider,
  useRblProviders,
  useUpsertRblProvider,
} from './useNexusRbl';

const EMPTY = {
  identifier: '',
  name: '',
  dns_zone: '',
  lookup_url: '',
  weight: 1,
  enabled: true,
  return_codes_text: '',
};

export function RblProvidersTab({ canWrite }) {
  const query = useRblProviders();
  const upsert = useUpsertRblProvider();
  const del = useDeleteRblProvider();
  const [editor, setEditor] = useState(null);

  const list = query.data?.providers ?? [];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Alert color="info" className="small flex-grow-1 mb-0 me-2">
          Spamhaus ZEN, Barracuda, SpamCop, SORBS sind Backend-seitig
          geseeded — Provider hier de-/aktivieren oder eigene ergänzen.
        </Alert>
        <HasPermission perms={['integrations.write']}>
          {canWrite && (
            <Button color="primary" size="sm" onClick={() => setEditor({ ...EMPTY })}>
              + Provider
            </Button>
          )}
        </HasPermission>
      </div>

      <DataTable
        loading={query.isLoading}
        error={query.error}
        empty={!query.isLoading && list.length === 0}
        emptyMessage="Noch keine Provider konfiguriert."
        onRetry={() => query.refetch()}
      >
        <thead>
          <tr>
            <th style={{ width: 140 }}>Identifier</th>
            <th>Name</th>
            <th style={{ width: 200 }}>DNS-Zone</th>
            <th style={{ width: 80 }}>Gewicht</th>
            <th style={{ width: 110 }}>Status</th>
            <th style={{ width: 180 }}>Lookup-URL</th>
            <th style={{ width: 180 }}>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {list.map((p) => (
            <tr key={p.identifier}>
              <td><code className="small">{p.identifier}</code></td>
              <td>{p.name}</td>
              <td className="small font-monospace">{p.dns_zone}</td>
              <td>{p.weight}</td>
              <td>
                {p.enabled ? (
                  <Badge color="success" pill>enabled</Badge>
                ) : (
                  <Badge color="secondary" pill>disabled</Badge>
                )}
              </td>
              <td className="small text-truncate" style={{ maxWidth: 180 }} title={p.lookup_url}>
                {(() => {
                  const u = safeExternalUrl(p.lookup_url?.replace('{ip}', '0.0.0.0'));
                  return u ? (
                    <a href={u} target="_blank" rel="noreferrer noopener">Vorlage ↗</a>
                  ) : '—';
                })()}
              </td>
              <td>
                <HasPermission perms={['integrations.write']}>
                  {canWrite && (
                    <ButtonGroup size="sm">
                      <Button color="info" outline onClick={() => setEditor(toEditorState(p))}>
                        Edit
                      </Button>
                      <Button
                        color={p.enabled ? 'warning' : 'success'}
                        outline
                        onClick={() => upsert.mutate({ ...stripReadOnly(p), enabled: !p.enabled })}
                        disabled={upsert.isPending}
                      >
                        {p.enabled ? 'Disable' : 'Enable'}
                      </Button>
                      <Button
                        color="danger"
                        outline
                        onClick={() => del.mutate({ identifier: p.identifier })}
                        disabled={del.isPending}
                      >
                        ✕
                      </Button>
                    </ButtonGroup>
                  )}
                </HasPermission>
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>

      {editor && (
        <ProviderEditor
          key={editor.identifier || '__new'}
          state={editor}
          isOpen={!!editor}
          onClose={() => setEditor(null)}
        />
      )}
    </div>
  );
}

function ProviderEditor({ state, isOpen, onClose }) {
  const upsert = useUpsertRblProvider();
  const [form, setForm] = useState(state);

  if (!state) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    let return_codes;
    try {
      return_codes = parseReturnCodes(form.return_codes_text);
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err.message);
      return;
    }
    await upsert.mutateAsync({
      identifier: form.identifier,
      name: form.name,
      dns_zone: form.dns_zone,
      lookup_url: form.lookup_url || null,
      weight: Number(form.weight) || 1,
      enabled: !!form.enabled,
      return_codes,
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>
        {state.identifier ? `Provider ${state.identifier} bearbeiten` : 'Neuer RBL-Provider'}
      </ModalHeader>
      <Form onSubmit={onSubmit}>
        <ModalBody>
          <FormGroup>
            <Label>Identifier (^[a-z0-9-]+$)</Label>
            <Input
              type="text"
              value={form.identifier}
              onChange={(e) => setForm((f) => ({ ...f, identifier: e.target.value }))}
              disabled={!!state.identifier}
              pattern="[-a-z0-9]+"
              required
            />
          </FormGroup>
          <FormGroup>
            <Label>Name</Label>
            <Input
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
            />
          </FormGroup>
          <FormGroup>
            <Label>DNS-Zone</Label>
            <Input
              type="text"
              value={form.dns_zone}
              onChange={(e) => setForm((f) => ({ ...f, dns_zone: e.target.value }))}
              placeholder="zen.spamhaus.org"
              required
            />
          </FormGroup>
          <FormGroup>
            <Label>Lookup-URL (optional, <code>{'{ip}'}</code>-Platzhalter)</Label>
            <Input
              type="url"
              value={form.lookup_url}
              onChange={(e) => setForm((f) => ({ ...f, lookup_url: e.target.value }))}
              placeholder="https://check.spamhaus.org/listed/?searchterm={ip}"
            />
          </FormGroup>
          <FormGroup>
            <Label>Gewicht (1-10)</Label>
            <Input
              type="number"
              min={1}
              max={10}
              value={form.weight}
              onChange={(e) => setForm((f) => ({ ...f, weight: e.target.value }))}
            />
          </FormGroup>
          <FormGroup>
            <Label>Return-Codes (eine pro Zeile: <code>127.0.0.X = Label</code>)</Label>
            <Input
              type="textarea"
              rows={4}
              value={form.return_codes_text}
              onChange={(e) => setForm((f) => ({ ...f, return_codes_text: e.target.value }))}
              placeholder={`127.0.0.2 = SBL\n127.0.0.4 = XBL-CBL`}
            />
          </FormGroup>
          <FormGroup check>
            <Input
              type="checkbox"
              id="rbl-enabled"
              checked={form.enabled}
              onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
            />
            <Label check for="rbl-enabled">Aktiv</Label>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={onClose}>Abbrechen</Button>
          <Button color="primary" type="submit" disabled={upsert.isPending}>
            {upsert.isPending ? 'Speichere…' : 'Speichern'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

function toEditorState(p) {
  return {
    identifier: p.identifier,
    name: p.name,
    dns_zone: p.dns_zone,
    lookup_url: p.lookup_url ?? '',
    weight: p.weight ?? 1,
    enabled: !!p.enabled,
    return_codes_text: Object.entries(p.return_codes ?? {})
      .map(([code, label]) => `${code} = ${label}`)
      .join('\n'),
  };
}

function stripReadOnly(p) {
  return {
    identifier: p.identifier,
    name: p.name,
    dns_zone: p.dns_zone,
    lookup_url: p.lookup_url ?? null,
    weight: p.weight ?? 1,
    return_codes: p.return_codes ?? {},
  };
}

function parseReturnCodes(text) {
  const out = {};
  for (const raw of (text ?? '').split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    const m = line.match(/^([0-9.]+)\s*=\s*(.+)$/);
    if (!m) throw new Error(`Return-Code-Zeile ungültig: "${line}"`);
    out[m[1]] = m[2].trim();
  }
  return out;
}
